import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { useApi, type MetricDefinition } from "../lib/api";
import { useTenant } from "../lib/tenant";

type Observation = {
  ts: string;
  value: number;
};

type ExtendedMetric = MetricDefinition & {
  expression?: string;
  dbt_model?: string;
  refresh_cron?: string;
  owner?: string;
  description?: string;
  external?: boolean;
  version?: number;
  updated_at?: string;
};

const DOMAIN_OPTIONS = ["finance", "sales", "operations", "hr", "marketing", "compliance"] as const;
const CLASSIFICATION_OPTIONS = ["public", "internal", "confidential", "restricted"] as const;
const CLASSIFICATION_TONE: Record<MetricDefinition["data_classification"], string> = {
  public: "good",
  internal: "muted",
  confidential: "warn",
  restricted: "bad",
};
const DIRECTION_LABEL: Record<MetricDefinition["direction"], string> = {
  up_is_favorable: "↑ besser",
  down_is_favorable: "↓ besser",
  neutral: "neutral",
};

export default function MetricsPage() {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [domain, setDomain] = useState<string | null>(null);
  const [classification, setClassification] = useState<MetricDefinition["data_classification"] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ["metrics", effectiveTenantId],
    queryFn: () => api.get<ExtendedMetric[]>("/metric-definitions"),
  });

  const detail = useQuery({
    queryKey: ["metrics", effectiveTenantId, selected],
    queryFn: () => api.get<ExtendedMetric>(`/metric-definitions/${selected}`),
    enabled: !!selected,
  });

  const observations = useQuery({
    queryKey: ["kpi-observations", effectiveTenantId, selected],
    queryFn: () =>
      api.get<Observation[]>(
        `/kpi-observations?metric_id=${encodeURIComponent(selected!)}`,
      ),
    enabled: !!selected,
  });

  const triggerDiscovery = useMutation({
    mutationFn: () =>
      api.post<{ run_id: string }>("/agent-runs/kpi-discovery/trigger", {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["metrics", effectiveTenantId] }),
  });

  const filtered = useMemo(() => {
    const base = list.data ?? [];
    const q = search.trim().toLowerCase();
    return base.filter((m) => {
      if (domain && m.domain !== domain) return false;
      if (classification && m.data_classification !== classification) return false;
      if (q) {
        const hay = `${m.name} ${m.display_name ?? ""} ${m.domain} ${m.unit}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [list.data, search, domain, classification]);

  const counts = useMemo(() => {
    const base = list.data ?? [];
    const byDomain: Record<string, number> = {};
    for (const m of base) byDomain[m.domain] = (byDomain[m.domain] ?? 0) + 1;
    return { total: base.length, byDomain };
  }, [list.data]);

  const fmtTarget = (m: ExtendedMetric) => {
    if (!m.target) return "—";
    const tol = m.target.tolerance_pct !== undefined ? ` ±${m.target.tolerance_pct}%` : "";
    return `${m.target.value} ${m.unit}${tol}`;
  };

  return (
    <div className="page metrics-page">
      <div className="metrics-head">
        <div>
          <h1>Kennzahlen</h1>
          <p className="subtitle">
            FR-02 Metric Contracts — versioniert, dbt-validiert, tenant-scoped.
            {counts.total > 0 && <> · {counts.total} aktive Definitionen.</>}
          </p>
        </div>
        <button
          className="btn"
          disabled={triggerDiscovery.isPending}
          onClick={() => triggerDiscovery.mutate()}
          title="KPI-Miner-Agent CP-111a starten"
        >
          {triggerDiscovery.isPending ? "Starte…" : "KPI-Discovery starten"}
        </button>
      </div>

      <div className="metrics-filters">
        <input
          type="search"
          placeholder="Suche Name/Display/Domain…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="metrics-search"
        />
        <span className="muted small">Domain:</span>
        <button
          className={`pill ${domain === null ? "accent" : "muted"}`}
          onClick={() => setDomain(null)}
        >
          alle
        </button>
        {DOMAIN_OPTIONS.map((d) => (
          <button
            key={d}
            className={`pill ${domain === d ? "accent" : "muted"}`}
            onClick={() => setDomain(d)}
          >
            {d}
            {counts.byDomain[d] && <> ({counts.byDomain[d]})</>}
          </button>
        ))}
        <span className="grow" />
        <span className="muted small">Klassifikation:</span>
        <button
          className={`pill ${classification === null ? "accent" : "muted"}`}
          onClick={() => setClassification(null)}
        >
          alle
        </button>
        {CLASSIFICATION_OPTIONS.map((c) => (
          <button
            key={c}
            className={`pill ${classification === c ? CLASSIFICATION_TONE[c] : "muted"}`}
            onClick={() => setClassification(c)}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="metrics-layout">
        <aside className="metrics-list">
          {list.isLoading && <p>Lade…</p>}
          {list.error && <p className="error">Die Kennzahlen-Definitionen konnten gerade nicht geladen werden. Bitte später erneut versuchen.</p>}
          {!list.isLoading && filtered.length === 0 && (
            <div className="empty-state">
              <p>Keine Metriken im Filter.</p>
              {counts.total === 0 && (
                <p className="small">
                  Noch keine Definitionen. „KPI-Discovery starten" oben rechts
                  triggert den Miner-Agent (CP-111a, deterministisch, kein LLM
                  bei v0).
                </p>
              )}
            </div>
          )}
          <ul className="run-list">
            {filtered.map((m) => (
              <li
                key={m.id}
                className={`run-item ${selected === m.id ? "expanded" : ""}`}
              >
                <button
                  type="button"
                  className="run-row"
                  onClick={() => setSelected(m.id)}
                >
                  <div className="metric-head">
                    <strong>{m.display_name ?? m.name}</strong>
                    <div className="small muted">{m.name}</div>
                  </div>
                  <span className="grow" />
                  <span className="pill muted">{m.domain}</span>
                  <span className={`pill ${CLASSIFICATION_TONE[m.data_classification]}`}>
                    {m.data_classification}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <section className="metrics-detail">
          {!selected && (
            <div className="empty-state">
              <p>Wählen Sie links eine Kennzahl aus.</p>
            </div>
          )}
          {selected && detail.isLoading && <p>Lade Details…</p>}
          {selected && detail.error && (
            <p className="error">Detail nicht verfügbar.</p>
          )}
          {selected && detail.data && (
            <MetricDetail
              metric={detail.data}
              observations={observations.data ?? []}
              fmtTarget={fmtTarget}
            />
          )}
        </section>
      </div>
    </div>
  );
}

function MetricDetail({
  metric,
  observations,
  fmtTarget,
}: {
  metric: ExtendedMetric;
  observations: Observation[];
  fmtTarget: (m: ExtendedMetric) => string;
}) {
  const chartData = useMemo(
    () =>
      observations.map((o) => ({
        ts: new Date(o.ts).toLocaleDateString("de-DE", {
          month: "short",
          day: "2-digit",
        }),
        value: o.value,
      })),
    [observations],
  );

  const last = observations[observations.length - 1];
  const prev = observations[observations.length - 2];
  const delta =
    last && prev && prev.value !== 0
      ? ((last.value - prev.value) / Math.abs(prev.value)) * 100
      : null;

  return (
    <article className="metric-detail">
      <header className="metric-detail-head">
        <div>
          <h2>{metric.display_name ?? metric.name}</h2>
          <div className="small muted">
            <code>{metric.name}</code>
            {metric.version !== undefined && <> · v{metric.version}</>}
            {metric.updated_at && (
              <> · aktualisiert {new Date(metric.updated_at).toLocaleString("de-DE")}</>
            )}
          </div>
        </div>
        <div className="metric-detail-pills">
          <span className="pill muted">{metric.domain}</span>
          <span className={`pill ${CLASSIFICATION_TONE[metric.data_classification]}`}>
            {metric.data_classification}
          </span>
          <span className="pill muted">{metric.granularity}</span>
          <span className="pill muted">{DIRECTION_LABEL[metric.direction]}</span>
          {metric.external && <span className="pill bad">external=true</span>}
        </div>
      </header>

      {metric.description && (
        <p className="metric-description">{metric.description}</p>
      )}

      <div className="metric-grid">
        <div>
          <div className="kv-label">Einheit</div>
          <div className="kv-value">{metric.unit}</div>
        </div>
        <div>
          <div className="kv-label">Target</div>
          <div className="kv-value">{fmtTarget(metric)}</div>
        </div>
        <div>
          <div className="kv-label">Owner</div>
          <div className="kv-value">{metric.owner ?? "—"}</div>
        </div>
        <div>
          <div className="kv-label">Refresh</div>
          <div className="kv-value">
            {metric.refresh_cron ? <code>{metric.refresh_cron}</code> : "—"}
          </div>
        </div>
        <div>
          <div className="kv-label">dbt Model</div>
          <div className="kv-value">
            {metric.dbt_model ? <code>{metric.dbt_model}</code> : "—"}
          </div>
        </div>
        <div>
          <div className="kv-label">Letzter Wert</div>
          <div className="kv-value">
            {last ? `${last.value.toLocaleString("de-DE")} ${metric.unit}` : "—"}
            {delta !== null && (
              <span
                className={`pill ${
                  (delta > 0 && metric.direction === "up_is_favorable") ||
                  (delta < 0 && metric.direction === "down_is_favorable")
                    ? "good"
                    : (delta > 0 && metric.direction === "down_is_favorable") ||
                      (delta < 0 && metric.direction === "up_is_favorable")
                    ? "bad"
                    : "muted"
                }`}
              >
                {delta > 0 ? "+" : ""}
                {delta.toFixed(1)}%
              </span>
            )}
          </div>
        </div>
      </div>

      {metric.expression && (
        <details className="metric-expression">
          <summary>SQL-Expression</summary>
          <pre>
            <code>{metric.expression}</code>
          </pre>
        </details>
      )}

      <section className="metric-chart">
        <div className="kv-label">Zeitreihe ({observations.length} Punkte)</div>
        {observations.length === 0 ? (
          <p className="muted small">
            Keine Observations geladen. Quellen anbinden via CP-105
            (Airbyte/Crawl4AI/Nimble) oder Upload-Gateway.
          </p>
        ) : (
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <LineChart data={chartData}>
                <XAxis dataKey="ts" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} width={50} />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="var(--accent)"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </section>
    </article>
  );
}
